/**
 * Image FX — effetto WebGL2 sulle immagini: turbolenza ambient sempre viva ("Live")
 * + liquid displacement al mouse. Tre effetti selezionabili (1 Liquid Ink, 2 Particle
 * Dust, 3 Halftone Press) e 5 parametri, tutti dai Theme settings.
 *
 * UN SOLO contesto WebGL2 condiviso: una canvas fixed a tutto schermo disegna ogni
 * tile visibile nel suo rettangolo (viewport + scissor), così una pagina con molte
 * immagini (fashion = 26) non esaurisce i contesti e "Live" può animarle tutte.
 * Solo le tile a schermo (IntersectionObserver) renderizzano; a riposo (Live off, no
 * hover) la tile non viene disegnata → resta l'<img> nativo sotto.
 *
 * Markup atteso (da snippets/image-fx-attrs.liquid) sul contenitore <figure>/<div>:
 *   <figure data-image-fx data-fx-src="https://.../image_600x.jpg"> <img …> </figure>
 *
 * Solo desktop con puntatore fine e motion abilitato: su touch e reduced-motion le
 * immagini restano <img> statici (nessun contesto WebGL creato).
 */
(function () {
  'use strict';

  const supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion || !supportsHover) return;

  const DPR = Math.min(window.devicePixelRatio || 1, 2);
  const FIELD = 128; // risoluzione campo liquido (ping-pong)
  const GRID = 112; // griglia particelle (GRID*GRID punti)
  const MARGIN = 0.13; // bleed della canvas oltre la tile (per lato)
  const INSET = MARGIN / (1 + 2 * MARGIN);
  const SPAN = 1 - 2 * INSET;

  const QUAD_VS = `#version 300 es
  precision highp float;
  layout(location=0) in vec2 a_pos;
  out vec2 v_uv;
  void main(){ v_uv = a_pos*0.5+0.5; gl_Position = vec4(a_pos,0.0,1.0); }`;

  const LUMA = `float luma(vec3 c){ return dot(c, vec3(0.299,0.587,0.114)); }`;
  const COVER = `uniform vec2 u_cover; vec2 cover(vec2 uv){ return (uv-0.5)*u_cover + 0.5; }`;

  // aggiornamento del campo liquido (diffusione + iniezione sotto il cursore)
  const F_FIELD = `#version 300 es
  precision highp float;
  in vec2 v_uv; out vec4 o;
  uniform sampler2D u_prev;
  uniform vec2 u_mouse; uniform float u_hover; uniform float u_inject; uniform vec2 u_texel; uniform float u_decay; uniform float u_radius;
  void main(){
    float c = texture(u_prev, v_uv).r;
    float n = texture(u_prev, v_uv+vec2(0.0,u_texel.y)).r;
    float s = texture(u_prev, v_uv-vec2(0.0,u_texel.y)).r;
    float e = texture(u_prev, v_uv+vec2(u_texel.x,0.0)).r;
    float w = texture(u_prev, v_uv-vec2(u_texel.x,0.0)).r;
    float blurred = c*0.4 + (n+s+e+w)*0.15;
    float field = blurred * u_decay;
    float d = distance(v_uv, u_mouse);
    float rad = mix(0.0025, 0.022, u_radius);
    field += exp(-d*d/rad) * u_inject * u_hover;
    o = vec4(clamp(field,0.0,1.0),0.0,0.0,1.0);
  }`;

  // passthrough: immagine esatta nella regione tile, trasparente fuori
  const F_PASS = `#version 300 es
  precision highp float;
  in vec2 v_uv; out vec4 o;
  uniform sampler2D u_img; uniform float u_inset;
  ${COVER}
  void main(){
    float span = 1.0 - 2.0*u_inset;
    vec2 imgUv = (v_uv - u_inset)/span;
    vec2 ed = min(imgUv, 1.0-imgUv);
    if(min(ed.x,ed.y) < 0.0){ o = vec4(0.0); return; }
    o = vec4(texture(u_img, cover(imgUv)).rgb, 1.0);
  }`;

  // 1 — liquid: rifrazione pura (niente inchiostro scuro), turbolenza continua, bordo morbido
  const F_LIQUID = `#version 300 es
  precision highp float;
  in vec2 v_uv; out vec4 o;
  uniform sampler2D u_img; uniform sampler2D u_field;
  uniform vec2 u_texel; uniform float u_hover; uniform float u_inset;
  uniform float u_time; uniform float u_intensity; uniform float u_turb; uniform float u_bleed; uniform float u_ambient;
  ${COVER}
  vec2 flow(vec2 p, float t){
    return vec2(
      sin(p.x*6.1 + t*0.9) + sin(p.y*4.7 - t*0.7),
      sin(p.y*5.7 - t*0.8) + sin(p.x*5.0 + t*0.6)
    );
  }
  void main(){
    float span = 1.0 - 2.0*u_inset;
    vec2 base = (v_uv - u_inset)/span;
    vec2 mxy = smoothstep(0.0,0.10,base) * smoothstep(0.0,0.10,1.0-base);
    float interior = clamp(mxy.x*mxy.y, 0.0, 1.0);
    float hx = texture(u_field, v_uv+vec2(u_texel.x,0.0)).r - texture(u_field, v_uv-vec2(u_texel.x,0.0)).r;
    float hy = texture(u_field, v_uv+vec2(0.0,u_texel.y)).r - texture(u_field, v_uv-vec2(0.0,u_texel.y)).r;
    vec2 grad = vec2(hx,hy);
    float th = max(u_hover, u_ambient);
    vec2 turb = flow(v_uv, u_time) * (u_turb*0.020) * th;
    float scale = mix(0.10, 0.95, u_intensity);
    vec2 disp = (grad*scale*u_hover + turb) * interior;
    vec2 imgUv = base - disp;
    vec3 col = texture(u_img, clamp(cover(imgUv),0.0,1.0)).rgb;
    vec2 ed = min(base, 1.0-base);
    float m = min(ed.x, ed.y);
    float band = mix(0.002, 0.02, u_bleed);
    float alpha = smoothstep(0.0, band, m);
    o = vec4(col, alpha);
  }`;

  // 3 — halftone press
  const F_HALFTONE = `#version 300 es
  precision highp float;
  in vec2 v_uv; out vec4 o;
  uniform sampler2D u_img; uniform float u_hover; uniform float u_time; uniform vec2 u_mouse; uniform float u_inset; uniform float u_intensity; uniform float u_radius;
  ${LUMA}
  ${COVER}
  const vec3 PAPER = vec3(0.988,0.988,0.984);
  mat2 rot(float a){ float s=sin(a),c=cos(a); return mat2(c,-s,s,c); }
  void main(){
    float span = 1.0 - 2.0*u_inset;
    vec2 imgUv = (v_uv - u_inset)/span;
    if(imgUv.x<0.0||imgUv.x>1.0||imgUv.y<0.0||imgUv.y>1.0){ o = vec4(0.0); return; }
    float L = luma(texture(u_img, cover(imgUv)).rgb);
    float angle = 0.32 + 0.05*sin(u_time*0.5);
    float cells = mix(22.0, 82.0, u_intensity);
    vec2 ruv  = rot(angle)*(imgUv-0.5)+0.5;
    vec2 grid = ruv*cells;
    vec2 cc   = (floor(grid)+0.5)/cells;
    vec2 sampleUV = rot(-angle)*(cc-0.5)+0.5;
    float Lc = luma(texture(u_img, cover(sampleUV)).rgb);
    float dd = length(fract(grid)-0.5);
    float lens = 1.0 + 0.7*exp(-distance(v_uv,u_mouse)*mix(12.0,3.5,u_radius))*u_hover;
    float radius = (1.0 - Lc) * 0.72 * lens;
    float ink = 1.0 - smoothstep(radius-0.04, radius+0.04, dd);
    vec3 htone = mix(PAPER, vec3(0.05), ink);
    o = vec4(mix(vec3(L), htone, u_hover),1.0);
  }`;

  // 2 — particle dust
  const P_VS = `#version 300 es
  precision highp float;
  layout(location=0) in vec2 a_uv;
  uniform sampler2D u_img; uniform vec2 u_mouse; uniform float u_hover; uniform float u_time; uniform float u_pt; uniform float u_inset; uniform float u_intensity; uniform float u_radius;
  out vec2 v_uv;
  ${LUMA}
  ${COVER}
  float hash(vec2 p){ return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453); }
  void main(){
    v_uv = cover(a_uv);
    float span = 1.0 - 2.0*u_inset;
    vec2 cuv = u_inset + a_uv*span;
    vec2 home = cuv*2.0-1.0;
    vec2 m = u_mouse*2.0-1.0;
    float rnd = hash(a_uv);
    float ang = rnd*6.2831 + u_time*0.6;
    vec2 jit = vec2(cos(ang),sin(ang))*0.04*rnd;
    float dist = distance(home, m);
    float pull = smoothstep(mix(0.9, 2.6, u_radius), 0.0, dist);
    vec2 target = mix(home, m + jit*2.2, pull*0.65 + 0.18);
    vec2 dest = target + jit;
    vec2 pos = home + (dest - home) * (u_hover * mix(0.8, 2.4, u_intensity));
    float L = luma(texture(u_img, v_uv).rgb);
    gl_Position = vec4(pos,0.0,1.0);
    gl_PointSize = u_pt * (0.45 + L*1.0) * (0.55 + 0.85*u_hover);
  }`;

  const P_FS = `#version 300 es
  precision highp float;
  in vec2 v_uv; out vec4 o;
  uniform sampler2D u_img;
  void main(){
    vec2 c = gl_PointCoord-0.5;
    if(dot(c,c) > 0.25) discard;
    o = vec4(texture(u_img, v_uv).rgb, 1.0);
  }`;

  function compile(gl, type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) console.error(gl.getShaderInfoLog(s), src);
    return s;
  }
  function program(gl, vs, fs) {
    const p = gl.createProgram();
    gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, vs));
    gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) console.error(gl.getProgramInfoLog(p));
    return p;
  }
  function uniforms(gl, p, names) {
    const u = {};
    for (const n of names) u[n] = gl.getUniformLocation(p, n);
    return u;
  }
  function readVar(name, fallback) {
    const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
    return Number.isFinite(v) ? v : fallback;
  }

  // Parametri globali dai Theme settings (CSS var). Rileggibili al reload del customizer.
  const params = { intensity: 0.3, radius: 0.45, turb: 0.3, trail: 0.5, bleed: 0.4 };
  let effect = 1;
  let ambient = true;
  function readParams() {
    params.intensity = readVar('--image-fx-intensity', 0.3);
    params.radius = readVar('--image-fx-radius', 0.45);
    params.turb = readVar('--image-fx-turbulence', 0.3);
    params.trail = readVar('--image-fx-trail', 0.5);
    params.bleed = readVar('--image-fx-bleed', 0.4);
    effect = Math.round(readVar('--image-fx-effect', 1));
    ambient = readVar('--image-fx-ambient', 1) >= 0.5;
  }

  const imgCache = new Map();
  function loadImage(src) {
    if (imgCache.has(src)) return imgCache.get(src);
    const p = new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
    imgCache.set(src, p);
    return p;
  }

  // Una tile = un elemento con la sua texture + campo liquido ping-pong. La canvas è condivisa.
  class Tile {
    constructor(gl, el) {
      this.gl = gl;
      this.el = el;
      this.imgW = 1;
      this.imgH = 1;
      this.ready = false;
      this.visible = false;
      this.hover = 0;
      this.target = 0;
      this.mx = 0.5;
      this.my = 0.5;
      this.lmx = 0.5;
      this.lmy = 0.5;
      this.inject = 0.14;
      this.tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      // campo liquido ping-pong
      this.fb = [0, 1].map(() => {
        const t = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, FIELD, FIELD, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
        const f = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, f);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
        return { t, f };
      });
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      this.cur = 0;
      // Sorgente: <video> (texture ridisegnata dal frame corrente) o immagine statica (data-fx-src).
      this.video = el.querySelector('video');
      this.isVideo = !!this.video;
      if (!this.isVideo) {
        const src = el.dataset.fxSrc;
        if (src) loadImage(src).then((img) => this.#upload(img)).catch(() => {});
      }
    }
    // Carica il frame corrente del <video> nella texture (una volta che ha dati). Chiamata dal
    // tick finché la tile è a schermo → l'effetto resta "vivo" seguendo il video.
    grabVideo() {
      const v = this.video;
      if (!v || v.readyState < 2 || !v.videoWidth) return;
      const gl = this.gl;
      this.imgW = v.videoWidth;
      this.imgH = v.videoHeight;
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, v);
        this.ready = true;
      } catch (e) {}
    }
    #upload(img) {
      const gl = this.gl;
      this.imgW = img.naturalWidth || 1;
      this.imgH = img.naturalHeight || 1;
      gl.bindTexture(gl.TEXTURE_2D, this.tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        this.ready = true;
      } catch (e) {}
    }
    setMouse(x, y) {
      this.mx = x;
      this.my = y;
      const d = Math.hypot(x - this.lmx, y - this.lmy);
      this.inject = Math.min(0.3, this.inject * 0.6 + d * 4.0 + 0.1);
      this.lmx = x;
      this.lmy = y;
    }
    cover(rw, rh) {
      const ri = this.imgW / this.imgH;
      const rb = rw / rh;
      return ri >= rb ? [rb / ri, 1] : [1, ri / rb];
    }
  }

  class Engine {
    constructor() {
      const canvas = document.createElement('canvas');
      canvas.className = 'image-fx-canvas';
      canvas.setAttribute('aria-hidden', 'true');
      document.body.appendChild(canvas);
      this.canvas = canvas;
      const gl = (this.gl = canvas.getContext('webgl2', {
        antialias: true,
        alpha: true,
        premultipliedAlpha: false,
        depth: false,
      }));
      if (!gl) throw new Error('no webgl2');

      // geometrie
      this.quadVAO = gl.createVertexArray();
      gl.bindVertexArray(this.quadVAO);
      const qb = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, qb);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

      this.ptVAO = gl.createVertexArray();
      gl.bindVertexArray(this.ptVAO);
      const uv = new Float32Array(GRID * GRID * 2);
      let k = 0;
      for (let y = 0; y < GRID; y++)
        for (let x = 0; x < GRID; x++) {
          uv[k++] = (x + 0.5) / GRID;
          uv[k++] = (y + 0.5) / GRID;
        }
      const pb = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, pb);
      gl.bufferData(gl.ARRAY_BUFFER, uv, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      this.ptCount = GRID * GRID;
      gl.bindVertexArray(null);

      this.pField = program(gl, QUAD_VS, F_FIELD);
      this.pPass = program(gl, QUAD_VS, F_PASS);
      this.pLiquid = program(gl, QUAD_VS, F_LIQUID);
      this.pHalftone = program(gl, QUAD_VS, F_HALFTONE);
      this.pPart = program(gl, P_VS, P_FS);
      this.uField = uniforms(gl, this.pField, ['u_prev', 'u_mouse', 'u_hover', 'u_inject', 'u_texel', 'u_decay', 'u_radius']);
      this.uPass = uniforms(gl, this.pPass, ['u_img', 'u_inset', 'u_cover']);
      this.uLiquid = uniforms(gl, this.pLiquid, ['u_img', 'u_field', 'u_texel', 'u_hover', 'u_inset', 'u_time', 'u_intensity', 'u_turb', 'u_bleed', 'u_ambient', 'u_cover']);
      this.uHalftone = uniforms(gl, this.pHalftone, ['u_img', 'u_hover', 'u_time', 'u_mouse', 'u_inset', 'u_intensity', 'u_radius', 'u_cover']);
      this.uPart = uniforms(gl, this.pPart, ['u_img', 'u_mouse', 'u_hover', 'u_time', 'u_pt', 'u_inset', 'u_intensity', 'u_radius', 'u_cover']);

      this.tiles = new Map();
      this.byHover = null;
      this.raf = null;
      this.t0 = performance.now();
      this.vw = 0;
      this.vh = 0;
      this.io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          const tile = this.tiles.get(e.target);
          if (tile) tile.visible = e.isIntersecting;
        }
        this.wake();
      });
      this.#bind();
      this.#scan();
      window.addEventListener('resize', () => this.wake(), { passive: true });
      window.addEventListener('scroll', () => this.wake(), { passive: true });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.wake();
      });
      this.wake();
    }

    #scan() {
      for (const el of document.querySelectorAll('[data-image-fx]')) {
        if (this.tiles.has(el)) continue;
        const tile = new Tile(this.gl, el);
        this.tiles.set(el, tile);
        this.io.observe(el);
        // Video: al primo frame (e all'avvio del play) risveglia il loop così la texture si popola.
        if (tile.isVideo) {
          tile.video.addEventListener('loadeddata', () => this.wake(), { passive: true });
          tile.video.addEventListener('play', () => this.wake(), { passive: true });
        }
      }
    }

    #bind() {
      document.addEventListener('pointerover', (e) => {
        const el = e.target instanceof Element ? e.target.closest('[data-image-fx]') : null;
        if (!el) return;
        const tile = this.tiles.get(el);
        if (tile) {
          this.byHover = tile;
          tile.target = 1;
          this.wake();
        }
      });
      document.addEventListener('pointerout', (e) => {
        const el = e.target instanceof Element ? e.target.closest('[data-image-fx]') : null;
        if (!el) return;
        if (el.contains(e.relatedTarget)) return;
        const tile = this.tiles.get(el);
        if (tile) {
          tile.target = 0;
          if (this.byHover === tile) this.byHover = null;
        }
      });
      document.addEventListener(
        'pointermove',
        (e) => {
          const tile = this.byHover;
          if (!tile) return;
          const r = tile.el.getBoundingClientRect();
          if (!r.width || !r.height) return;
          tile.setMouse((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
        },
        { passive: true }
      );
    }

    #resize() {
      const w = Math.round(window.innerWidth * DPR);
      const h = Math.round(window.innerHeight * DPR);
      if (w !== this.canvas.width || h !== this.canvas.height) {
        this.canvas.width = w;
        this.canvas.height = h;
      }
      this.vw = window.innerWidth;
      this.vh = window.innerHeight;
    }

    wake() {
      if (this.raf == null) this.raf = requestAnimationFrame(this.tick);
    }

    #drawTile(tile, now) {
      const gl = this.gl;
      const up = 0.12;
      const down = 0.01 + (1 - params.trail) * 0.11;
      const rate = tile.target > tile.hover ? up : down;
      tile.hover += (tile.target - tile.hover) * rate;
      tile.inject = Math.max(0.1, tile.inject * 0.9);
      const active = tile.hover >= 0.015;
      if (!active && !ambient) {
        tile.hover = tile.hover < 0.004 ? 0 : tile.hover;
        return false; // niente disegno → resta l'<img>
      }

      const r = tile.el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > this.vh || r.right < 0 || r.left > this.vw || !r.width) return true;

      // rettangolo canvas allargato del margine (bleed), in px device, origine in basso-sx
      const ew = r.width * (1 + 2 * MARGIN) * DPR;
      const eh = r.height * (1 + 2 * MARGIN) * DPR;
      const ex = (r.left - r.width * MARGIN) * DPR;
      const ey = this.canvas.height - (r.top - r.height * MARGIN + r.height * (1 + 2 * MARGIN)) * DPR;
      const t = (now - this.t0) / 1000;
      const ambLvl = ambient ? 0.6 : 0;
      const cov = tile.cover(r.width, r.height);
      const mU = INSET + tile.mx * SPAN;
      const mV = 1 - (INSET + tile.my * SPAN);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tile.tex);

      if (active && effect === 2) {
        gl.viewport(ex, ey, ew, eh);
        gl.scissor(ex, ey, ew, eh);
        gl.useProgram(this.pPart);
        gl.bindVertexArray(this.ptVAO);
        gl.uniform1i(this.uPart.u_img, 0);
        gl.uniform2f(this.uPart.u_mouse, mU, mV);
        gl.uniform1f(this.uPart.u_hover, tile.hover);
        gl.uniform1f(this.uPart.u_time, t);
        gl.uniform1f(this.uPart.u_pt, (ew * SPAN / GRID) * 1.7);
        gl.uniform1f(this.uPart.u_inset, INSET);
        gl.uniform1f(this.uPart.u_intensity, params.intensity);
        gl.uniform1f(this.uPart.u_radius, params.radius);
        gl.uniform2f(this.uPart.u_cover, cov[0], cov[1]);
        gl.drawArrays(gl.POINTS, 0, this.ptCount);
      } else if (active && effect === 3) {
        gl.viewport(ex, ey, ew, eh);
        gl.scissor(ex, ey, ew, eh);
        gl.useProgram(this.pHalftone);
        gl.bindVertexArray(this.quadVAO);
        gl.uniform1i(this.uHalftone.u_img, 0);
        gl.uniform1f(this.uHalftone.u_hover, tile.hover);
        gl.uniform1f(this.uHalftone.u_time, t);
        gl.uniform2f(this.uHalftone.u_mouse, mU, mV);
        gl.uniform1f(this.uHalftone.u_inset, INSET);
        gl.uniform1f(this.uHalftone.u_intensity, params.intensity);
        gl.uniform1f(this.uHalftone.u_radius, params.radius);
        gl.uniform2f(this.uHalftone.u_cover, cov[0], cov[1]);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      } else {
        // liquid (fx 1) su hover, oppure ondulazione ambient "viva" a riposo per ogni effetto
        if (active && effect === 1) {
          const dst = tile.fb[tile.cur];
          const srcF = tile.fb[1 - tile.cur];
          gl.disable(gl.SCISSOR_TEST);
          gl.bindFramebuffer(gl.FRAMEBUFFER, dst.f);
          gl.viewport(0, 0, FIELD, FIELD);
          gl.useProgram(this.pField);
          gl.bindVertexArray(this.quadVAO);
          gl.activeTexture(gl.TEXTURE1);
          gl.bindTexture(gl.TEXTURE_2D, srcF.t);
          gl.uniform1i(this.uField.u_prev, 1);
          gl.uniform2f(this.uField.u_mouse, mU, mV);
          gl.uniform1f(this.uField.u_hover, tile.hover);
          gl.uniform1f(this.uField.u_inject, tile.inject);
          gl.uniform1f(this.uField.u_decay, 0.9 + 0.095 * params.trail);
          gl.uniform1f(this.uField.u_radius, params.radius);
          gl.uniform2f(this.uField.u_texel, 1 / FIELD, 1 / FIELD);
          gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
          tile.cur = 1 - tile.cur;
          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
          gl.enable(gl.SCISSOR_TEST);
        }
        gl.viewport(ex, ey, ew, eh);
        gl.scissor(ex, ey, ew, eh);
        gl.useProgram(this.pLiquid);
        gl.bindVertexArray(this.quadVAO);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, tile.tex);
        gl.uniform1i(this.uLiquid.u_img, 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, tile.fb[1 - tile.cur].t);
        gl.uniform1i(this.uLiquid.u_field, 1);
        gl.uniform2f(this.uLiquid.u_texel, 1 / FIELD, 1 / FIELD);
        gl.uniform1f(this.uLiquid.u_hover, effect === 1 ? tile.hover : 0);
        gl.uniform1f(this.uLiquid.u_inset, INSET);
        gl.uniform1f(this.uLiquid.u_time, t);
        gl.uniform1f(this.uLiquid.u_intensity, params.intensity);
        gl.uniform1f(this.uLiquid.u_turb, params.turb);
        gl.uniform1f(this.uLiquid.u_bleed, params.bleed);
        gl.uniform1f(this.uLiquid.u_ambient, ambLvl);
        gl.uniform2f(this.uLiquid.u_cover, cov[0], cov[1]);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }
      return true;
    }

    tick = (now) => {
      const gl = this.gl;
      this.#resize();
      gl.disable(gl.SCISSOR_TEST);
      gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.enable(gl.SCISSOR_TEST);

      let busy = false;
      for (const tile of this.tiles.values()) {
        // Video: aggiorna la texture dal frame corrente finché la tile è a schermo.
        if (tile.isVideo && tile.visible) tile.grabVideo();
        if (!tile.ready || !tile.visible) {
          if (tile.target === 0) tile.hover = 0;
          continue;
        }
        const drew = this.#drawTile(tile, now);
        if (drew || tile.hover > 0.004 || tile.target > 0) busy = true;
      }
      gl.bindVertexArray(null);

      if (busy && !document.hidden) this.raf = requestAnimationFrame(this.tick);
      else this.raf = null;
    };

    refresh() {
      readParams();
      this.#scan();
      this.wake();
    }
  }

  let engine = null;
  function boot() {
    readParams();
    if (engine) {
      engine.refresh();
      return;
    }
    if (!document.querySelector('[data-image-fx]')) return;
    try {
      engine = new Engine();
    } catch (e) {
      engine = null;
      console.warn('[image-fx]', e);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
